import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GitCompare } from "lucide-react";

import {
  type IxdPreviewCompareSourceSpec,
  type IxdPreviewCompareSourceType,
} from "@/apis/compare/route";
import { type UserActionFile } from "@/apis/logs/route";
import { LeftSidebar } from "@/components/app-shell";
import { ExistingIxdReportPicker } from "@/components/existing-ixd-report-picker";
import { FileUploadSection } from "@/components/file-upload-section";
import { IxdUploadNotice } from "@/components/ixd-upload-notice";
import {
  BetaFeatureMark,
  BetaFeatureSegmentControl,
  type BetaFeatureSegmentControlItem,
} from "@/components/beta-feature";
import { Button } from "@/toggle-ui-kit/button";
import { PARAM_SOURCE_TYPE } from "@/utils/ixd-preview-compare/compare-url";
import { startIxdIxdCompare } from "@/utils/ixd-preview-compare/result-tab";

/**
 * 한쪽 비교 소스. reportFilename 은 기존 IXD 를 골랐을 때 화면에 뜬 원본 파일명으로,
 * spec 에는 id 만 담기므로 행동 로깅용으로 따로 들고 있는다.
 */
interface CompareSource {
  spec: IxdPreviewCompareSourceSpec;
  reportFilename?: string;
}

/**
 * 행동 로깅에 실을 파일. 업로드 소스는 그 자리에서 비교만 하고 버리므로 이름만 남고,
 * 기존 IXD 는 저장된 원본이 있어 백엔드가 id 로 링크를 만든다.
 * 최종산출물(워크북)은 파일이 아니라 아무것도 싣지 않는다.
 */
const sourceToActionFile = (source: CompareSource): UserActionFile | null => {
  const { spec, reportFilename } = source;
  switch (spec.type) {
    case "xlsx":
      return spec.xlsFile ? { filename: spec.xlsFile.name } : null;
    case "ixd_file":
      return spec.ixdFile ? { filename: spec.ixdFile.name } : null;
    case "ixd_report":
      return spec.ixdReportId
        ? { filename: reportFilename ?? "", ixdReportId: spec.ixdReportId }
        : null;
    case "editable_treeview":
      return null;
  }
};

const SOURCE_TYPE_ITEMS = [
  { value: "ixd_file", label: "IXD 업로드", isBetaFeature: false },
  {
    value: "ixd_report",
    label: "편집용 IXD",
    isBetaFeature: false,
  },
  {
    value: "editable_treeview",
    label: "최종산출물",
    isBetaFeature: true,
  },
  { value: "xlsx", label: "XBRL 편집기 xlsx", isBetaFeature: true },
] satisfies BetaFeatureSegmentControlItem<IxdPreviewCompareSourceType>[];

const isSpecComplete = (spec: IxdPreviewCompareSourceSpec): boolean => {
  switch (spec.type) {
    case "xlsx":
      return !!spec.xlsFile;
    case "editable_treeview":
      return !!spec.workbookId?.trim();
    case "ixd_report":
      return !!spec.ixdReportId;
    case "ixd_file":
      return !!spec.ixdFile;
  }
};

/** 진입 파라미터로 두 카드의 시작 종류를 정한다. 모르는 값이면 기본(IXD 업로드). */
const entrySourceType = (params: URLSearchParams) =>
  SOURCE_TYPE_ITEMS.find((item) => item.value === params.get(PARAM_SOURCE_TYPE))
    ?.value ?? "ixd_file";

export const IxdPreviewComparePage = () => {
  const [searchParams] = useSearchParams();
  // 첫 렌더에만 쓴다 — 이후 종류는 사용자가 세그먼트로 바꾼다
  const [entryType] = useState(() => entrySourceType(searchParams));
  const [src, setSrc] = useState<CompareSource>({ spec: { type: entryType } });
  const [trg, setTrg] = useState<CompareSource>({ spec: { type: entryType } });

  const canRun = isSpecComplete(src.spec) && isSpecComplete(trg.spec);

  const handleViewInNewTab = () =>
    startIxdIxdCompare(
      src.spec,
      trg.spec,
      [src, trg].map(sourceToActionFile).filter((file) => file !== null),
    );

  return (
    <div className="flex h-screen w-full">
      <LeftSidebar activeMenu="ixd-ixd-compare" />
      <div className="flex-1 min-w-0 flex flex-col items-center overflow-auto bg-white py-32">
        <div className="flex flex-col gap-9 items-center w-full p-16 min-w-[784px]">
          {/* 헤더 섹션 */}
          <div className="flex flex-col gap-2 items-start max-w-[1144px] w-full">
            <h1 className="title-2-bold text-label-normal">IXD 보고서 비교</h1>
            <p className="body-1-normal-medium text-label-neutral">
              두 IXD 보고서를 대사하여 표·항목의 추가/삭제/변경 내역을
              확인하세요. 선택 후 새 탭에서 결과가 열립니다.
            </p>
          </div>

          {/* 버튼 + 카드 컨테이너 */}
          <div className="flex flex-col gap-5 items-center w-full">
            {/* 버튼 행 */}
            <div className="flex items-center justify-end gap-2 max-w-[1144px] w-full">
              <Button
                variant="solid"
                color="secondary"
                size={36}
                leftIcon={<GitCompare size={18} />}
                disabled={!canRun}
                onClick={handleViewInNewTab}
              >
                IXD 보고서 비교
              </Button>
            </div>

            {/* 카드 행 */}
            <div className="flex gap-6 items-start justify-center w-full max-w-[1144px]">
              <SourceCard
                title="기준 IXD"
                source={src}
                onChange={setSrc}
                accentColor="blue"
              />
              <SourceCard
                title="비교 IXD"
                source={trg}
                onChange={setTrg}
                accentColor="purple"
              />
            </div>

            <IxdUploadNotice
              accepts=".ixd, .xls, .xlsx"
              className="w-full max-w-[1144px]"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

interface SourceCardProps {
  title: string;
  source: CompareSource;
  onChange: (next: CompareSource) => void;
  accentColor: "blue" | "purple";
}

const SourceCard = ({
  title,
  source,
  onChange,
  accentColor,
}: SourceCardProps) => {
  const handleTypeChange = (type: IxdPreviewCompareSourceType) => {
    onChange({ spec: { type } });
  };

  return (
    <div className="flex-1 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="label-1-normal-medium text-label-normal">{title}</span>
      </div>
      <BetaFeatureSegmentControl
        items={SOURCE_TYPE_ITEMS}
        value={source.spec.type}
        onChange={handleTypeChange}
        size="small"
      />
      <SourceInput
        source={source}
        onChange={onChange}
        accentColor={accentColor}
      />
    </div>
  );
};

interface SourceInputProps {
  source: CompareSource;
  onChange: (next: CompareSource) => void;
  accentColor: "blue" | "purple";
}

const SourceInput = ({ source, onChange, accentColor }: SourceInputProps) => {
  const { spec } = source;
  switch (spec.type) {
    case "xlsx":
      return (
        <BetaFeatureMark>
          <FileUploadSection
            label="xls/xlsx 파일"
            accept=".xls,.xlsx"
            onFileSelect={(file) =>
              onChange({ spec: { type: "xlsx", xlsFile: file } })
            }
            isLoading={false}
            fileName={spec.xlsFile?.name}
            description="XBRL 편집기에서 export한 xls"
            accentColor={accentColor}
          />
        </BetaFeatureMark>
      );
    case "editable_treeview":
      return (
        <BetaFeatureMark>
          <div className="flex-1 rounded-[10px] border border-neutral-200 bg-white p-4">
            <label
              htmlFor={`workbook-id-${accentColor}`}
              className="block label-1-normal-medium text-label-normal mb-2"
            >
              워크북 ID
            </label>
            <input
              id={`workbook-id-${accentColor}`}
              type="text"
              value={spec.workbookId ?? ""}
              onChange={(e) =>
                onChange({
                  spec: {
                    type: "editable_treeview",
                    workbookId: e.target.value,
                  },
                })
              }
              placeholder="workbook_id 입력"
              className="w-full h-11 px-4 rounded-lg border border-neutral-200 bg-white text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="caption-2-regular text-label-alternative mt-2">
              워크북에 저장된 편집기 결과(최종산출물)를 비교 대상으로
              사용합니다.
            </p>
          </div>
        </BetaFeatureMark>
      );
    case "ixd_report":
      return (
        <ExistingIxdReportPicker
          selectedReportId={spec.ixdReportId ?? null}
          onSelect={(reportId, report) =>
            onChange({
              spec: {
                type: "ixd_report",
                ixdReportId: reportId ?? undefined,
              },
              reportFilename: report?.originalFilename ?? undefined,
            })
          }
        />
      );
    case "ixd_file":
      return (
        <FileUploadSection
          label="IXD 파일"
          accept=".ixd"
          onFileSelect={(file) =>
            onChange({ spec: { type: "ixd_file", ixdFile: file } })
          }
          isLoading={false}
          fileName={spec.ixdFile?.name}
          description="비교할 IXD 파일을 업로드합니다 (저장되지 않음)"
          accentColor={accentColor}
        />
      );
  }
};

export default IxdPreviewComparePage;
